import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
    consumeCalendarCampaignOAuthReturn,
    isCalendarCampaignContext,
    isCalendarCampaignOAuthReturn,
} from '@/lib/calendarCampaignFlow';
import {
    getSelectSheetsPathAfterOAuth,
    isCampaignSheetsContext
} from '@/lib/googleSheetsFlow';

/**
 * Google OAuth Callback Component
 * Handles the redirect from Google OAuth and notifies the parent window if it's a popup.
 * This prevents the entire dashboard from loading inside the small popup window.
 */
const GoogleCallback: React.FC = () => {
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const authStatus = searchParams.get('auth');
        const context = searchParams.get('context') || 'general';

        console.log('Google OAuth Callback received:', {
            authStatus,
            context
        });

        // OAuth successful
        if (authStatus === 'success') {
            const isCampaignCalendar =
                isCalendarCampaignContext(context) ||
                (
                    context === 'calendar' &&
                    isCalendarCampaignOAuthReturn()
                );

            // Campaign wizard: always return to renewal-builder
            if (isCampaignCalendar) {
                const returnPath = consumeCalendarCampaignOAuthReturn();

                if (window.opener) {
                    try {
                        window.opener.location.href = returnPath;
                    } catch (e) {
                        console.error('Error redirecting opener:', e);
                    }

                    setTimeout(() => {
                        window.close();
                    }, 300);
                } else {
                    window.location.href = returnPath;
                }

                return;
            }

            if (window.opener) {
                try {
                    window.opener.postMessage(
                        {
                            type: 'google_oauth_success',
                            context: context
                        },
                        window.location.origin
                    );

                    setTimeout(() => {
                        window.close();
                    }, 800);
                } catch (e) {
                    console.error(
                        'Error communicating with parent window:',
                        e
                    );

                    setTimeout(() => {
                        window.close();
                    }, 2000);
                }
            } else {
                if (context === 'calendar') {
                    window.location.href = '/calendar?auth=success';
                } else if (isCampaignSheetsContext(context)) {
                    window.location.href =
                        getSelectSheetsPathAfterOAuth(context);
                } else {
                    window.location.href =
                        '/google-sheets?auth=success';
                }
            }

            return;
        }

        // OAuth cancelled by user
        if (authStatus === 'cancelled') {
            console.log(
                'Google OAuth authorization cancelled:',
                { context }
            );

            const redirectPath =
                context === 'calendar'
                    ? '/calendar'
                    : context === 'sheets' || context === 'integrations'
                        ? '/google-sheets'
                        : '/';

            if (window.opener) {
                try {
                    window.opener.location.href = redirectPath;
                } catch (e) {
                    console.error(
                        'Error redirecting parent window:',
                        e
                    );
                }

                setTimeout(() => {
                    window.close();
                }, 300);
            } else {
                window.location.href = redirectPath;
            }

            return;
        }

        // Handle real OAuth errors
        if (window.opener) {
            window.opener.postMessage(
                {
                    type: 'google_oauth_error',
                    error: 'Authentication failed',
                    context: context
                },
                window.location.origin
            );

            setTimeout(() => {
                window.close();
            }, 2000);
        } else {
            if (context === 'calendar') {
                window.location.href =
                    '/calendar?auth=error';
            } else {
                window.location.href =
                    '/google-sheets?auth=error';
            }
        }
    }, [searchParams]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-indigo-100">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Conectando con Google...
                </h2>

                <p className="text-gray-600">
                    Estamos finalizando la configuración técnica.
                </p>

                <p className="text-xs text-gray-400 mt-6 pt-4 border-t border-gray-100">
                    Esta ventana se cerrará automáticamente en unos segundos.
                </p>
            </div>
        </div>
    );
};

export default GoogleCallback;